import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { VanityAPI } from '../services/api';

// Configure worker - Use explicit version match or CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// CSS for react-pdf to prevent layout shifts
const pdfWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%'
};

const MobilePDFViewer: React.FC = () => {
    const { issueId } = useParams<{ issueId: string }>();
    const navigate = useNavigate();

    // PDF State
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [issueSlug, setIssueSlug] = useState<string | null>(null);

    const [progress, setProgress] = useState(0);

    // Responsive Scaling
    const [pageScale, setPageScale] = useState(1.0);
    const [containerDimensions, setContainerDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

    useEffect(() => {
        const handleResize = () => {
            setContainerDimensions({ width: window.innerWidth, height: window.innerHeight });
            // Mobile: larger scale for clarity (1.5), Desktop: standard (1.0)
            setPageScale(window.innerWidth < 768 ? 1.5 : 1.0);
        };

        handleResize(); // Init
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load Issue & PDF URL
    useEffect(() => {
        const load = async () => {
            if (!issueId) {
                setError('No issue ID provided');
                setLoading(false);
                return;
            }

            try {
                // Try fetching direct issue first by ID or slug
                const issues = await VanityAPI.getAllMagazines();
                const issue = issues.find(i => i.id === issueId || i.slug === issueId);

                if (!issue || !issue.pdfUrl) {
                    setError('PDF not found for this issue.');
                    setLoading(false);
                    return;
                }

                setIssueSlug(issue.slug || null);
                setPdfUrl(issue.pdfUrl);
                // Note: We do NOT set loading(false) here. 
                // We wait for the PDF Document to load (onDocumentLoadSuccess)
            } catch (err) {
                console.error("PDF Load Error:", err);
                setError('Failed to load magazine data.');
                setLoading(false);
            }
        };

        load();
    }, [issueId]);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setLoading(false);
    };

    const onDocumentLoadProgress = ({ loaded, total }: { loaded: number; total: number }) => {
        if (total > 0) {
            setProgress(Math.round((loaded / total) * 100));
        }
    };

    const onDocumentLoadError = (err: Error) => {
        console.error("PDF Render Error:", err);
        setError('Failed to load PDF file. It may be corrupt or missing.');
        setLoading(false);
    };

    const changePage = (offset: number) => {
        setPageNumber(prev => Math.min(Math.max(1, prev + offset), numPages));
    };

    const handleBack = () => {
        if (issueSlug) {
            navigate(`/archive/${issueSlug}`);
        } else {
            navigate(-1);
        }
    };

    // Error State (keeps early return for fatal errors)
    if (error) {
        return (
            <div className="fixed inset-0 bg-vanity-charcoal flex items-center justify-center z-50">
                <div className="text-center max-w-xs px-6">
                    <p className="text-white font-serif mb-4">{error}</p>
                    <button onClick={handleBack} className="bg-white text-black px-6 py-2 rounded-full text-xs font-bold uppercase tracking-widest">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black z-50 overflow-hidden flex flex-col">

            {/* LOADING OVERLAY (Fixes Infinite Loop by allowing Document to render underneath) */}
            {loading && (
                <div className="absolute inset-0 z-[60] bg-vanity-charcoal flex items-center justify-center">
                    <div className="text-center w-64">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vanity-pink mb-4"></div>
                        <p className="text-white font-serif tracking-widest text-xs uppercase mb-4">Loading Magazine...</p>

                        {/* Progress Bar */}
                        <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                            <div
                                className="bg-vanity-pink h-full transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <p className="text-white/40 font-mono text-[10px] mt-2">{progress}%</p>
                    </div>
                </div>
            )}

            {/* Header / Controls */}
            <div className="absolute top-0 left-0 right-0 z-20 p-4 flex justify-between items-center pointer-events-none">
                <button
                    onClick={handleBack}
                    className="pointer-events-auto bg-black/50 backdrop-blur-md text-white/80 w-10 h-10 rounded-full flex items-center justify-center hover:bg-vanity-pink transition-colors"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>

                <div className="pointer-events-auto bg-black/50 backdrop-blur-md px-4 py-1.5 rounded-full text-white font-mono text-xs font-bold">
                    {pageNumber} / {numPages}
                </div>

                <div className="w-10"></div> {/* Spacer for center alignment */}
            </div>

            {/* Viewer Area */}
            <div className="flex-grow relative w-full h-full flex items-center justify-center bg-black">
                {pdfUrl && (
                    <TransformWrapper
                        initialScale={1}
                        minScale={1}
                        maxScale={4}
                        centerOnInit={true}
                        doubleClick={{ disabled: false, mode: 'reset' }}
                    >
                        {({ zoomIn, zoomOut, resetTransform }) => (
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                                contentStyle={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadProgress={onDocumentLoadProgress}
                                    onLoadError={onDocumentLoadError}
                                    loading={null} // Handled by overlay
                                    className="scale-in-center"
                                >
                                    <Page
                                        pageNumber={pageNumber}
                                        scale={pageScale}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                        loading={
                                            <div style={{ width: containerDimensions.width, height: containerDimensions.height * 0.6 }} className="bg-white/5 animate-pulse flex items-center justify-center">
                                                <span className="text-white/20 text-[10px] uppercase">Rendering Page {pageNumber}</span>
                                            </div>
                                        }
                                        width={window.innerWidth < 768 ? window.innerWidth : undefined} // Force width on mobile
                                        height={window.innerWidth >= 768 ? window.innerHeight : undefined} // Force height on desktop
                                        className="shadow-2xl"
                                    />
                                </Document>
                            </TransformComponent>
                        )}
                    </TransformWrapper>
                )}
            </div>

            {/* Bottom Nav Overlay */}
            <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-8 pointer-events-none">
                <button
                    onClick={() => changePage(-1)}
                    disabled={pageNumber <= 1}
                    className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white disabled:opacity-0 transition-all hover:bg-vanity-pink"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>
                <button
                    onClick={() => changePage(1)}
                    disabled={pageNumber >= numPages}
                    className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white disabled:opacity-0 transition-all hover:bg-vanity-pink"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default MobilePDFViewer;
